import React, { useState, useEffect } from 'react';
import { Button, Tag, Typography, Modal, message, Empty, Space, Table, Tooltip } from 'antd';
import {
	PlusOutlined,
	EditOutlined,
	DeleteOutlined,
	PlayCircleOutlined,
	PauseCircleOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import { OutboundCampaign } from '@/types/TOutbound';
import OutboundCampaignModal from './OutboundTaskModal';
import { useOutbound } from 'src/Hooks/SmartCRM/useOutbound';

const { Title, Text } = Typography;
const { confirm } = Modal;

const formatDate = (dateString: string): string => {
	if (!dateString) {
		return '-';
	}
	return new Date(dateString).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

const getDayAbbr = (dayIndex: number): string => {
	const dayAbbrs = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	return dayAbbrs[dayIndex] || '';
};

const getStatusColor = (status: string): string => {
	switch (status) {
		case 'Active':
			return 'green';
		case 'Paused':
			return 'orange';
		case 'Draft':
			return 'blue';
		default:
			return 'gray';
	}
};

const formatScheduleTime = (startTime?: string, endTime?: string): string => {
	if (!startTime || !endTime) {
		return 'Not scheduled';
	}
	return `${startTime} - ${endTime}`;
};

const renderDays = (daysOfWeek?: number[]) => {
	if (!daysOfWeek || daysOfWeek.length === 0) {
		return '-';
	}
	return daysOfWeek.map((day) => getDayAbbr(day)).join(', ');
};

const OutboundTasks: React.FC = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [editingTask, setEditingTask] = useState<OutboundCampaign | null>(null);
	const [refreshing, setRefreshing] = useState(false);

	const { campaignsArray, updateStatusMutation, deleteTaskMutation, getTaskMutation } =
		useOutbound();

	useEffect(() => {
		handleRefresh();
	}, []);

	const handleRefresh = async () => {
		setRefreshing(true);
		try {
			await getTaskMutation.mutateAsync();
		} catch (_error) {
			message.error('Failed to fetch tasks');
		} finally {
			setRefreshing(false);
		}
	};

	const handleStatusChange = async (taskId: string, newStatus: 'Active' | 'Paused' | 'Draft') => {
		try {
			await updateStatusMutation.mutateAsync({ taskId, status: newStatus });
			message.success(`Task ${newStatus.toLowerCase()} successfully`);
			await handleRefresh();
		} catch (_error) {
			message.error('Failed to update status');
		}
	};

	const handleDelete = (task: OutboundCampaign) => {
		confirm({
			title: 'Delete Task',
			content: `Are you sure you want to delete "${task.name}"?`,
			okText: 'Delete',
			okType: 'danger',
			onOk: async () => {
				try {
					if (task.id) {
						await deleteTaskMutation.mutateAsync(task.id);
						await handleRefresh();
					}
				} catch (_error) {
					message.error('Failed to delete task');
				}
			},
		});
	};

	const handleEdit = (task: OutboundCampaign) => {
		setEditingTask(task);
		setIsModalOpen(true);
	};

	const handleCreate = () => {
		setEditingTask(null);
		setIsModalOpen(true);
	};

	const columns = [
		{
			title: 'Name',
			dataIndex: 'name',
			key: 'name',
			width: 200,
			render: (text: string, record: OutboundCampaign) => (
				<div>
					<Text strong>{text}</Text>
					<br />
					<Text type="secondary" className="text-xs">
						{record.goal ? record.goal.substring(0, 40) + '...' : 'No goal set'}
					</Text>
				</div>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (status: string, record: OutboundCampaign) => (
				<div>
					<Tag color={getStatusColor(status || 'Draft')}>{status || 'Draft'}</Tag>
					<br />
					<div className="mt-1">
						{status === 'Active' ? (
							<Button
								size="small"
								danger
								icon={<PauseCircleOutlined />}
								loading={updateStatusMutation.isPending}
								onClick={() => record.id && handleStatusChange(record.id, 'Paused')}
							>
								Pause
							</Button>
						) : (
							<Button
								type="primary"
								size="small"
								icon={<PlayCircleOutlined />}
								loading={updateStatusMutation.isPending}
								onClick={() => record.id && handleStatusChange(record.id, 'Active')}
							>
								Start
							</Button>
						)}
					</div>
				</div>
			),
		},
		{
			title: 'Schedule',
			key: 'schedule',
			width: 150,
			render: (record: OutboundCampaign) => (
				<div>
					<Text className="text-sm">
						{formatScheduleTime(record.schedule?.startTime, record.schedule?.endTime)}
					</Text>
					<br />
					<Text type="secondary" className="text-xs">
						{record.schedule?.timezone || 'UTC'}
					</Text>
				</div>
			),
		},
		{
			title: 'Days',
			key: 'days',
			width: 120,
			render: (record: OutboundCampaign) => (
				<Text className="text-sm">{renderDays(record.schedule?.daysOfWeek)}</Text>
			),
		},
		{
			title: 'Workflows',
			key: 'workflows',
			width: 100,
			render: (record: OutboundCampaign) => (
				<Text className="text-sm">{record.workflows?.length || 0} workflows</Text>
			),
		},
		{
			title: 'Rules',
			key: 'rules',
			width: 80,
			render: (record: OutboundCampaign) => (
				<Text className="text-sm">{record.enrollmentRules?.length || 0} rules</Text>
			),
		},
		{
			title: 'Created',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 120,
			render: (date: string) => <Text className="text-xs">{formatDate(date)}</Text>,
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			render: (record: OutboundCampaign) => (
				<Space>
					<Tooltip title="Edit">
						<Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
					</Tooltip>
					<Tooltip title="Delete">
						<Button
							type="link"
							danger
							icon={<DeleteOutlined />}
							loading={deleteTaskMutation.isPending}
							onClick={() => handleDelete(record)}
						/>
					</Tooltip>
				</Space>
			),
		},
	];

	const isLoading = getTaskMutation.isPending || refreshing;

	return (
		<div className="p-6 bg-white">
			<div className="max-w-full">
				<div className="flex justify-between items-center mb-6">
					<div>
						<Title level={2} className="mb-1">
							Outbound Tasks
						</Title>
						<Text type="secondary">Manage your automated outreach tasks</Text>
					</div>
					<Space>
						<Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={refreshing}>
							Refresh
						</Button>
						<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
							New Task
						</Button>
					</Space>
				</div>

				<Table
					columns={columns}
					dataSource={campaignsArray}
					rowKey="id"
					loading={isLoading}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} tasks`,
					}}
					locale={{
						emptyText: (
							<Empty description="No tasks found" image={Empty.PRESENTED_IMAGE_SIMPLE}>
								<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
									Create First Task
								</Button>
							</Empty>
						),
					}}
					size="middle"
					scroll={{ x: 800 }}
				/>

				<OutboundCampaignModal
					open={isModalOpen}
					onClose={() => {
						setIsModalOpen(false);
						setEditingTask(null);
						handleRefresh();
					}}
					editingTask={editingTask}
				/>
			</div>
		</div>
	);
};

export default OutboundTasks;
